import { useState, useMemo, useCallback, Fragment } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  SearchBox,
  Button,
  Checkbox,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  Spinner,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Document20Regular,
  Globe20Regular,
  Search20Regular,
  Database20Regular,
  Delete20Regular,
  Delete16Regular,
  ArrowClockwise20Regular,
  ChevronDown20Regular,
  ChevronRight16Regular,
  ChevronDown16Regular,
  Library24Regular,
  Add20Regular,
  ArrowUpload16Regular,
  DocumentText16Regular,
} from '@fluentui/react-icons';
import { formatDateTime } from '@/utils/dateTime';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useAppsInProject } from '@/hooks/useApps';
import { ProjectOverviewHeader } from '@/components/shared/ProjectOverviewHeader';
import { PageSection } from '@/components/shared/PageSection';

import { AddFileDialog } from '@/components/KnowledgeSources/AddFileDialog';
// TODO: re-enable when ready
// import { ConnectFoundryIQDialog } from '@/components/KnowledgeSources/ConnectFoundryIQDialog';
// import { ConnectAISearchDialog } from '@/components/KnowledgeSources/ConnectAISearchDialog';
import { ConnectWorkIQDialog } from '@/components/KnowledgeSources/ConnectWorkIQDialog';
import {
  useKnowledgeHubs,
  getHubArtifacts,
  useCreateFileHub,
  // useCreateFoundryIQHub,
  // useCreateAISearchHub,
  useDeleteKnowledgeHubs,
  useDeleteArtifact,
  useUploadArtifacts,
} from '@/hooks/useKnowledgeHubs';
import type { KnowledgeHubType, KnowledgeHubStatus, KnowledgeArtifactStatus } from '@/types/knowledgeHub';
import { usePermission, useIsProjectOwner } from '@/hooks/useUserPermissions';

// --- Types ---

type SourceType = KnowledgeHubType;
type SourceStatus = KnowledgeHubStatus;

// --- Styles ---

const useStyles = makeStyles({
  page: {
    // Outer padding + horizontal layout come from `ProjectLayout.mainContent`.
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  quickActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
  },
  actionCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    minWidth: '160px',
    transitionProperty: 'border-color, background-color',
    transitionDuration: '150ms',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outlineColor: tokens.colorBrandStroke1,
      outlineWidth: tokens.strokeWidthThick,
      outlineStyle: 'solid',
      outlineOffset: '-2px',
    },
  },
  actionIcon: {
    color: tokens.colorNeutralForeground3,
  },
  actionLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  searchBox: {
    minWidth: '240px',
  },
  tableContainer: {
    flex: 1,
    paddingInline: tokens.spacingHorizontalXXL,
    paddingBlock: tokens.spacingVerticalS,
  },
  emptyTableContainer: {

  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerCell: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    textAlign: 'left' as const,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  headerCellSortable: {
    cursor: 'pointer',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  row: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  selectedRow: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
  },
  cell: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    verticalAlign: 'middle' as const,
  },
  checkboxCell: {
    width: '40px',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    verticalAlign: 'middle' as const,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  typeIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  name: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  metadata: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  statusReady: {
    color: tokens.colorPaletteGreenForeground1,
  },
  statusProvisioning: {
    color: tokens.colorPaletteYellowForeground2,
  },
  statusError: {
    color: tokens.colorPaletteRedForeground1,
  },
  statusPending: {
    color: tokens.colorNeutralForeground3,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '10%',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXXL,
    flex: 1,
  },
  emptyIcon: {
    fontSize: '64px',
    color: tokens.colorNeutralForeground3,
  },
  emptyTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  emptySubtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
  },
  expandToggle: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    color: tokens.colorNeutralForeground3,
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  artifactRow: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  artifactCell: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    paddingInlineStart: tokens.spacingHorizontalXXXL,
  },
  artifactList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingBlock: tokens.spacingVerticalXS,
  },
  artifactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  artifactActions: {
    marginInlineStart: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  artifactIcon: {
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  artifactCount: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  artifactToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBlockEnd: tokens.spacingVerticalXS,
  },
});

// --- Helpers ---

const sourceTypeLabels: Record<SourceType, string> = {
  file: 'Built-in',
  'foundryiq': 'FoundryIQ',
  'ai-search': 'AI Search',
  workiq: 'WorkIQ',
};

const SourceTypeIcon = ({ type }: { type: SourceType }) => {
  switch (type) {
    case 'file':
      return <Document20Regular />;
    case 'foundryiq':
      return <Database20Regular />;
    case 'ai-search':
      return <Search20Regular />;
    case 'workiq':
      return <Globe20Regular />;
  }
};

const statusLabel = (status: SourceStatus): string => {
  switch (status) {
    case 'ready':
      return 'Ready';
    case 'provisioning':
      return 'Provisioning…';
    case 'error':
      return 'Error';
    case 'pending':
      return 'Pending';
    default:
      return 'Ready';
  }
};

const artifactStatusLabel = (status: KnowledgeArtifactStatus): string => {
  switch (status) {
    case 'Completed':
      return 'Indexed';
    case 'InProgress':
      return 'Indexing…';
    case 'Failed':
      return 'Error';
    case 'Deleting':
      return 'Deleting…';
  }
};

const artifactStatusClass = (status: KnowledgeArtifactStatus, styles: ReturnType<typeof useStyles>): string => {
  switch (status) {
    case 'Completed':
      return styles.statusReady;
    case 'InProgress':
    case 'Deleting':
      return styles.statusProvisioning;
    case 'Failed':
      return styles.statusError;
  }
};



// --- Component ---

export const KnowledgeSourcesPage = () => {
  const styles = useStyles();
  const { projectName: routeProjectName, project } = useCurrentProject();
  const { data: apps } = useAppsInProject(routeProjectName ?? '');
  useDocumentTitle(['Knowledge', routeProjectName]);
  const { data: isAdmin } = useIsProjectOwner(routeProjectName ?? '');
  const { data: userHasAccess } = usePermission(routeProjectName ?? '', undefined, ['Contributor', 'Author']);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<SourceType | 'all'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAddFile, setShowAddFile] = useState(false);
  const [uploadTargetHub, setUploadTargetHub] = useState<string | null>(null);
  // TODO: re-enable FoundryIQ/AI Search state when ready
  // const [showFoundryIQ, setShowFoundryIQ] = useState(false);
  // const [showAISearch, setShowAISearch] = useState(false);
  const [showWorkIQ, setShowWorkIQ] = useState(false);
  const [expandedHub, setExpandedHub] = useState<string | null>(null);

  // Data hooks
  const { data: hubs = [], isLoading, refetch } = useKnowledgeHubs();
  const createFileHubMutation = useCreateFileHub();
  // TODO: re-enable when FoundryIQ/AI Search are ready
  // const createFoundryIQMutation = useCreateFoundryIQHub();
  // const createAISearchMutation = useCreateAISearchHub();
  const deleteHubsMutation = useDeleteKnowledgeHubs();
  const deleteArtifactMutation = useDeleteArtifact();
  const uploadArtifactsMutation = useUploadArtifacts();

  const userCanCreate = useMemo(() => isAdmin || userHasAccess, [isAdmin, userHasAccess]);
  
  // Read artifacts from the expanded hub payload, but preserve the difference
  // between "no artifacts" and "artifacts were not embedded in this response".
  const expandedHubData = useMemo(
    () => hubs.find((hub) => hub.name === expandedHub) ?? null,
    [hubs, expandedHub]
  );

  const artifactsLoadState = useMemo<'idle' | 'loaded' | 'notLoaded'>(() => {
    if (!expandedHubData) {
      return 'idle';
    }

    if (
      expandedHubData.artifacts === undefined &&
      (expandedHubData.properties.artifactCount ?? 0) > 0
    ) {
      return 'notLoaded';
    }

    return 'loaded';
  }, [expandedHubData]);

  const artifacts = useMemo(
    () => (artifactsLoadState === 'loaded' ? getHubArtifacts(hubs, expandedHub) : []),
    [artifactsLoadState, hubs, expandedHub]
  );

  // Map KnowledgeHub[] to a flat shape for the table
  const sources = useMemo(
    () =>
      hubs.map((hub) => {
        const inProgress = hub.artifacts?.filter((a) => a.properties.uploadStatus === 'InProgress').length ?? 0;
        const total = hub.artifacts?.length ?? 0;
        return {
          id: hub.id,
          name: hub.name,
          description: hub.properties.description,
          type: (hub.properties.sourceType ?? hub.type) as SourceType,
          status: hub.properties.status as SourceStatus,
          artifactCount: hub.properties.artifactCount ?? 0,
          processingCount: inProgress,
          totalArtifacts: total,
          createdOn: hub.properties.createdAt
            ? formatDateTime(hub.properties.createdAt, 'locale')
            : '—',
        };
      }),
    [hubs]
  );

  const filtered = useMemo(() => {
    let result = sources;
    if (typeFilter !== 'all') {
      result = result.filter((s) => s.type === typeFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((s) => s.name.toLowerCase().includes(q));
    }
    return result;
  }, [sources, typeFilter, search]);

  const allSelected = filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id));
  const visibleSelectedCount = filtered.filter((s) => selectedIds.has(s.id)).length;

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map((s) => s.id)));
    }
  }, [allSelected, filtered]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const statusClass = (status: SourceStatus) => {
    switch (status) {
      case 'ready':
        return styles.statusReady;
      case 'provisioning':
        return styles.statusProvisioning;
      case 'error':
        return styles.statusError;
      case 'pending':
        return styles.statusPending;
      default:
        return styles.statusReady;
    }
  };

  // Collect existing hub names for the dialog
  // Only Built-in (file) hubs can receive file uploads
  const existingHubs = useMemo(
    () => [...new Set(sources.filter((s) => s.type === 'file').map((s) => s.name))],
    [sources]
  );

  const toggleExpand = useCallback((hubName: string) => {
    setExpandedHub((prev) => (prev === hubName ? null : hubName));
  }, []);

  const handleUploadToHub = useCallback((_hubName: string, files: File[], _description?: string, fileDescriptions?: Record<string, string>) => {
    if (!uploadTargetHub) return;
    uploadArtifactsMutation.mutate(
      { hubName: uploadTargetHub, files, ...(fileDescriptions ? { fileDescriptions } : {}) },
      { onSettled: () => setUploadTargetHub(null) }
    );
  }, [uploadArtifactsMutation, uploadTargetHub]);

  const handleAddFileSubmit = useCallback((hubName: string, files: File[], description?: string, fileDescriptions?: Record<string, string>) => {
    createFileHubMutation.mutate(
      { request: { hubName, ...(description ? { description } : {}) }, files, ...(fileDescriptions ? { fileDescriptions } : {}) },
      { onSettled: () => setShowAddFile(false) }
    );
  }, [createFileHubMutation]);

  // TODO: re-enable FoundryIQ submit handler when ready
  // const handleFoundryIQSubmit = useCallback((config: Parameters<typeof ConnectFoundryIQDialog>[0]['onSubmit'] extends (c: infer C) => void ? C : never) => {
  //   createFoundryIQMutation.mutate(
  //     {
  //       hubName: config.hubName,
  //       config: {
  //         endpoint: config.searchServiceEndpoint,
  //         knowledgeBaseName: config.knowledgeBaseName,
  //       },
  //     },
  //     { onSettled: () => setShowFoundryIQ(false) }
  //   );
  // }, [createFoundryIQMutation]);

  // TODO: re-enable AI Search submit handler when ready
  // const handleAISearchSubmit = useCallback((config: Parameters<typeof ConnectAISearchDialog>[0]['onSubmit'] extends (c: infer C) => void ? C : never) => {
  //   createAISearchMutation.mutate(
  //     {
  //       hubName: config.hubName,
  //       config: {
  //         endpoint: config.searchServiceEndpoint,
  //         indexName: config.knowledgeBaseName,
  //       },
  //     },
  //     { onSettled: () => setShowAISearch(false) }
  //   );
  // }, [createAISearchMutation]);

  const handleWorkIQSubmit = useCallback((config: Parameters<typeof ConnectWorkIQDialog>[0]['onSubmit'] extends (c: infer C) => void ? C : never) => {
    // TODO: WorkIQ hub creation not yet implemented in service
    console.log('Connect WorkIQ:', config);
    setShowWorkIQ(false);
  }, []);

  const handleDelete = useCallback(() => {
    const names = sources
      .filter((s) => selectedIds.has(s.id))
      .map((s) => s.name);
    if (names.length === 0) return;
    deleteHubsMutation.mutate(names, {
      onSuccess: () => setSelectedIds(new Set()),
    });
  }, [sources, selectedIds, deleteHubsMutation]);

  // No project selected — knowledge hubs are scoped to a project
  if (!routeProjectName) {
    return (
      <div className={styles.page}>
        <div className={styles.empty} role="status">
          <Library24Regular className={styles.emptyIcon} aria-hidden />
          <Text className={styles.emptyTitle}>No project selected</Text>
          <Text className={styles.emptySubtitle}>
            Select a project from the context picker to manage knowledge sources.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {routeProjectName && (
        <ProjectOverviewHeader
          name={routeProjectName}
          project={project}
          appCount={apps?.length ?? 0}
        />
      )}

      <PageSection
        title="Knowledge"
        description="Add knowledge sources to build a knowledge base that the agent uses to generate accurate, context-aware responses and insights."
      >
        {/* Quick-action cards */}
        {userCanCreate && <div className={styles.quickActions}>
          <div
            className={styles.actionCard}
            role="button"
            tabIndex={0}
            aria-label="Add file"
            onClick={() => setShowAddFile(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setShowAddFile(true);
              }
            }}
          >
            <Document20Regular className={styles.actionIcon} />
            <Text className={styles.actionLabel}>Add file</Text>
          </div>
          {/* TODO: re-enable FoundryIQ action card when ready
          <div
            className={styles.actionCard}
            role="button"
            tabIndex={0}
            aria-label="Connect to FoundryIQ"
            onClick={() => setShowFoundryIQ(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setShowFoundryIQ(true);
              }
            }}
          >
            <Database20Regular className={styles.actionIcon} />
            <Text className={styles.actionLabel}>Connect to FoundryIQ</Text>
          </div>
          */}
          {/* TODO: re-enable AI Search action card when ready
          <div
            className={styles.actionCard}
            role="button"
            tabIndex={0}
            aria-label="Connect to AI Search"
            onClick={() => setShowAISearch(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setShowAISearch(true);
              }
            }}
          >
            <Search20Regular className={styles.actionIcon} />
            <Text className={styles.actionLabel}>Connect to AI Search</Text>
          </div>
          */}
          {/* TODO: re-enable WorkIQ action card when ready
          <div
            className={styles.actionCard}
            role="button"
            tabIndex={0}
            aria-label="Connect to WorkIQ"
            onClick={() => setShowWorkIQ(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setShowWorkIQ(true);
              }
            }}
          >
            <Globe20Regular className={styles.actionIcon} />
            <Text className={styles.actionLabel}>Connect to WorkIQ</Text>
          </div>
          */}
        </div>}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Tooltip content="Delete selected" relationship="label">
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              disabled={visibleSelectedCount === 0 || deleteHubsMutation.isPending || !userCanCreate }
              aria-label="Delete selected"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Tooltip>
          <SearchBox
            className={styles.searchBox}
            placeholder="Search knowledge sources"
            value={search}
            onChange={(_e, data) => setSearch(data.value)}
          />
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button appearance="outline" size="small" icon={<ChevronDown20Regular />} iconPosition="after">
                Type : {typeFilter === 'all' ? 'All' : sourceTypeLabels[typeFilter]}
              </Button>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setTypeFilter('all')}>All</MenuItem>
                <MenuItem onClick={() => setTypeFilter('file')}>Built-in</MenuItem>
                {/* TODO: re-enable FoundryIQ filter when ready */}
                {/* <MenuItem onClick={() => setTypeFilter('foundryiq')}>FoundryIQ</MenuItem> */}
                {/* TODO: re-enable AI Search filter when ready */}
                {/* <MenuItem onClick={() => setTypeFilter('ai-search')}>AI Search</MenuItem> */}
                {/* TODO: re-enable WorkIQ filter when ready */}
                {/* <MenuItem onClick={() => setTypeFilter('workiq')}>WorkIQ</MenuItem> */}
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
        <div className={styles.toolbarRight}>
          <Tooltip content="Refresh" relationship="label">
            <Button
              appearance="subtle"
              icon={<ArrowClockwise20Regular />}
              size="small"
              aria-label="Refresh"
              onClick={() => refetch()}
            />
          </Tooltip>
          {isLoading && <Spinner size="tiny" label="Loading…" />}
        </div>
      </div>

      {/* Table header (always shown) */}
      <div className={filtered.length === 0 ? styles.emptyTableContainer : styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.checkboxCell}>
                <Checkbox
                  checked={allSelected ? true : visibleSelectedCount > 0 ? 'mixed' : false}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              <th className={mergeClasses(styles.headerCell, styles.headerCellSortable)}>Name ↕</th>
              <th className={styles.headerCell}>Description</th>
              <th className={styles.headerCell}>Status</th>
              <th className={styles.headerCell}>Type</th>
              <th className={styles.headerCell}>Artifacts</th>
              <th className={styles.headerCell}>Created on</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((source) => {
              const isExpanded = expandedHub === source.name;
              return (
                <Fragment key={source.id}>
                  <tr
                    className={mergeClasses(styles.row, selectedIds.has(source.id) && styles.selectedRow)}
                  >
                    <td className={styles.checkboxCell}>
                      <Checkbox
                        checked={selectedIds.has(source.id)}
                        onChange={() => toggleOne(source.id)}
                        aria-label={`Select ${source.name}`}
                      />
                    </td>
                    <td className={styles.cell}>
                      <span className={styles.nameCell}>
                        <span className={styles.typeIcon}>
                          <SourceTypeIcon type={source.type} />
                        </span>
                        <Text className={styles.name}>{source.name}</Text>
                      </span>
                    </td>
                    <td className={styles.cell}>
                      <Text className={styles.metadata}>{source.description ?? '—'}</Text>
                    </td>
                    <td className={styles.cell}>
                      <Text className={mergeClasses(styles.metadata, statusClass(source.status))}>
                        {statusLabel(source.status)}
                      </Text>
                      {source.processingCount > 0 && (
                        <Text className={mergeClasses(styles.metadata, styles.statusProvisioning)}>
                          {` (${source.processingCount} of ${source.totalArtifacts} processing)`}
                        </Text>
                      )}
                    </td>
                    <td className={styles.cell}>
                      <Text className={styles.metadata}>{sourceTypeLabels[source.type]}</Text>
                    </td>
                    <td className={styles.cell}>
                      {source.artifactCount > 0 ? (
                        <span
                          className={mergeClasses(styles.metadata, styles.artifactCount, styles.expandToggle)}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-label={`${source.artifactCount} artifacts, click to ${isExpanded ? 'collapse' : 'expand'}`}
                          onClick={() => toggleExpand(source.name)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleExpand(source.name);
                            }
                          }}
                        >
                          {isExpanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                          {source.artifactCount}
                        </span>
                      ) : (
                        <Text className={styles.metadata}>0</Text>
                      )}
                    </td>
                    <td className={styles.cell}>
                      <Text className={styles.metadata}>{source.createdOn}</Text>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={styles.artifactRow}>
                      <td className={styles.artifactCell} colSpan={7}>
                        {isLoading ? (
                          <div className={styles.artifactList}>
                            <Spinner size="tiny" />
                            <Text className={styles.metadata}>Loading artifacts...</Text>
                          </div>
                        ) : artifacts.length === 0 ? (
                          <Text className={styles.metadata}>No artifacts found.</Text>
                        ) : (
                          <div className={styles.artifactList}>
                            {artifacts.map((artifact) => {
                              // Guard against legacy API responses that may not include a properties bag.
                              const uploadStatus = (artifact.properties as { uploadStatus?: KnowledgeArtifactStatus } | undefined)?.uploadStatus ?? 'InProgress';
                              const description = artifact.properties?.description;
                              return (
                                <div key={artifact.name} className={styles.artifactItem}>
                                  <DocumentText16Regular className={styles.artifactIcon} />
                                  <Text>{artifact.name}</Text>
                                  {description && (
                                    <Text className={styles.metadata}>{description}</Text>
                                  )}
                                  <Text className={mergeClasses(styles.metadata, artifactStatusClass(uploadStatus, styles))}>
                                    {artifactStatusLabel(uploadStatus)}
                                  </Text>
                                  {userCanCreate && (
                                    <span className={styles.artifactActions}>
                                      <Button
                                        appearance="subtle"
                                        size="small"
                                        icon={<Delete16Regular />}
                                        title="Delete artifact"
                                        aria-label={`Delete artifact ${artifact.name}`}
                                        disabled={uploadStatus === 'Deleting'}
                                        onClick={() => {
                                          deleteArtifactMutation.mutate({
                                            hubName: source.name,
                                            artifactName: artifact.name,
                                          });
                                        }}
                                      />
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {userCanCreate && (
                          <div className={styles.artifactToolbar}>
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<ArrowUpload16Regular />}
                              onClick={() => setUploadTargetHub(source.name)}
                            >
                              Upload files
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty state — show messaging for all users, action button only for those with create permission */}
      {filtered.length === 0 && (
        <div className={styles.empty}>
          <Library24Regular className={styles.emptyIcon} aria-hidden />
          <Text className={styles.emptyTitle}>Ground responses and insights with knowledge</Text>
          <Text className={styles.emptySubtitle}>Add sources that the agent will reference for accuracy.</Text>
          {userCanCreate && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button appearance="primary" icon={<Add20Regular />}>
                  Add knowledge source
                  <ChevronDown20Regular />
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem icon={<Document20Regular />} onClick={() => setShowAddFile(true)}>Add file</MenuItem>
                  {/* TODO: re-enable FoundryIQ menu item when ready */}
                  {/* <MenuItem icon={<Database20Regular />} onClick={() => setShowFoundryIQ(true)}>Connect to FoundryIQ</MenuItem> */}
                  {/* TODO: re-enable AI Search menu item when ready */}
                  {/* <MenuItem icon={<Search20Regular />} onClick={() => setShowAISearch(true)}>Connect to AI Search</MenuItem> */}
                  {/* TODO: re-enable WorkIQ menu item when ready */}
                  {/* <MenuItem icon={<Globe20Regular />} onClick={() => setShowWorkIQ(true)}>Connect to WorkIQ</MenuItem> */}
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
        </div>
      )}
      </PageSection>

      {/* Dialogs */}
      <AddFileDialog
        open={showAddFile}
        existingHubs={existingHubs}
        isSubmitting={createFileHubMutation.isPending}
        onSubmit={handleAddFileSubmit}
        onCancel={() => setShowAddFile(false)}
      />
      <AddFileDialog
        open={uploadTargetHub !== null}
        existingHubs={existingHubs}
        isSubmitting={uploadArtifactsMutation.isPending}
        {...(uploadTargetHub ? { targetHub: uploadTargetHub } : {})}
        onSubmit={handleUploadToHub}
        onCancel={() => setUploadTargetHub(null)}
      />
      {/* TODO: re-enable FoundryIQ dialog when ready */}
      {/* <ConnectFoundryIQDialog
        open={showFoundryIQ}
        onSubmit={handleFoundryIQSubmit}
        onCancel={() => setShowFoundryIQ(false)}
      /> */}
      {/* TODO: re-enable AI Search dialog when ready */}
      {/* <ConnectAISearchDialog
        open={showAISearch}
        onSubmit={handleAISearchSubmit}
        onCancel={() => setShowAISearch(false)}
      /> */}
      <ConnectWorkIQDialog
        open={showWorkIQ}
        onSubmit={handleWorkIQSubmit}
        onCancel={() => setShowWorkIQ(false)}
      />
    </div>
  );
};
